import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import useUserAccountValues from '@/accountSettings/utils/useUserAccountValues';
import {
  createOfferBuilderInitialState,
  OfferBuilderProvider,
} from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { OfferReviewLayoutSkeleton } from '@/enterprise/components/OfferBuilder/OfferReview/OfferReviewLayout';
import { SetRateModalProvider } from '@/enterprise/components/OfferBuilder/OfferReview/SetRateModalContext';
import { RateMap } from '@/enterprise/components/OfferBuilder/rateHelpers';
import {
  ensureWorkingTakerMarkets,
  updateWorkingTakerMarketsWithRates,
} from '@/enterprise/components/OfferBuilder/utils';
import useDraftOffer, { DraftOfferData, DraftOfferType, DraftType } from '@/enterprise/data/useDraftOffer';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { useTakerMarketsInDraftOffer } from '../common/useTakerMarketsInDraftOffer';
import { DraftOfferError } from './DraftOfferError';

const DraftOfferLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { email } = useUserAccountValues();

  const isOnConfirmationPage = location.pathname.includes('/confirmation');

  return (
    <SetRateModalProvider>
      <div className="flex flex-col items-start justify-between gap-6 pb-10 md:flex-row md:items-center">
        {isOnConfirmationPage ? (
          <div className="w-full text-center">
            <h1 className="m-0 text-4xl">{t('builder.thankYouForSubmittingOffer')}</h1>
            <div className="mt-4 font-medium">
              {t('offerConfirmation.title', {
                email,
              })}
            </div>
            <div className="mt-1 text-sm text-text-secondary">{t('offerConfirmation.subtitle')}</div>
          </div>
        ) : (
          <h1 className="m-0 text-4xl">{t('core.earlyPayOffer')}</h1>
        )}
      </div>
      <Outlet />
    </SetRateModalProvider>
  );
};

const DraftOfferCore = ({ draftOfferData }: { draftOfferData: DraftOfferData }) => {
  const { trackEnterpriseEvent } = useReporting();

  const takerMarketsInOffer = useTakerMarketsInDraftOffer(draftOfferData);

  const offerType = draftOfferData?.type as DraftOfferType;
  const draftType = draftOfferData?.draftType as DraftType;

  useEffectOnce(() => {
    trackEnterpriseEvent('draft-offer::viewed', {
      draftOfferUuid: draftOfferData.uuid,
      type: offerType,
      draftType: draftType,
      draftSource: draftOfferData.draftSource,
    });
  });

  // Extract common rate type from first market (all markets have same type due to validation in DraftOfferComponent)
  // There is at least one rate information due to validation in DraftOfferComponent
  const commonRateType = draftOfferData.rateInformations[0].rateInformation.rateType;

  // We show REF rates as APR in most of the UI (top-level state)
  // Per-market rateInformations keep the original rate type ('REF')
  const displayRateType = commonRateType === 'REF' ? 'APR' : commonRateType;
  const topLevelRateInfo = { rate: null, rateType: displayRateType };

  // Populate working taker markets with rate info from draft offer config
  const rateMap: RateMap =
    draftOfferData?.rateInformations.reduce((map, ri) => {
      map[`${ri.marketUuid}-${ri.supplierDivisionUuid}`] = {
        rate: ri.rateInformation.rate,
        rateType: ri.rateInformation.rateType === 'REF' ? 'APR' : ri.rateInformation.rateType,
      };
      return map;
    }, {} as RateMap) ?? {};

  const workingTakerMarkets = updateWorkingTakerMarketsWithRates(
    ensureWorkingTakerMarkets({}, takerMarketsInOffer),
    rateMap
  );

  const draftState = createOfferBuilderInitialState({
    builderVariant: 'draft',
    frequency: draftOfferData?.frequency === 'one-time' ? 'oneTime' : 'recurring',
    ...topLevelRateInfo,
    selected: takerMarketsInOffer,
    workingTakerMarkets,
    reviewOnly: true,
    disableNavigation: true,
    ...(offerType === 'PRICE_DISCOVERY' && { pricingStrategy: 'nameYourRate' }),
    ...(offerType === 'EXPRESS_ACCEPT' && { pricingStrategy: 'expressAccept' }),
    draftOfferProperties: {
      draftOfferUuid: draftOfferData.uuid,
      offerType,
      draftType,
      termExpirationDate: draftOfferData.termExpirationDate,
      rateInformations: draftOfferData.rateInformations ?? [],
      pricingModifiers: draftOfferData.pricingModifiers,
      draftSource: draftOfferData.draftSource,
      frequency: draftOfferData.frequency,
    },
  });

  return (
    <OfferBuilderProvider initialState={draftState} takerMarkets={takerMarketsInOffer}>
      <DraftOfferLayout />
    </OfferBuilderProvider>
  );
};

const DraftOfferComponent = () => {
  const params = useParams<{ draftOfferUuid: string }>();
  const { draftOfferUuid = '' } = params;
  const [searchParams] = useSearchParams();
  const draftSource = searchParams.get('draftSource');

  const groupingKey = draftSource === 'smb' ? ('MAKER_ORG_CURRENCY' as const) : undefined;
  const { data: draftOfferData } = useDraftOffer(draftOfferUuid, groupingKey);

  if (!draftOfferData) {
    return (
      <DraftOfferError
        errorType="INVALID"
        trackEventMessage={`No draft offer data available from uuid: ${draftOfferUuid}`}
      />
    );
  }

  if (draftOfferData.status !== 'READY_FOR_SUBMISSION') {
    return (
      <DraftOfferError
        errorType="EXPIRED"
        trackEventMessage={`Draft offer status (${draftOfferData.status}) is not valid for draft submission.`}
        draftType={draftOfferData.draftType}
        type={draftOfferData.type}
      />
    );
  }

  // Valid types are PREFERRED, PRICE_DISCOVERY, BENCHMARK, EXPRESS_ACCEPT, and STATIC
  if (
    draftOfferData.type !== 'PREFERRED' &&
    draftOfferData.type !== 'PRICE_DISCOVERY' &&
    draftOfferData.type !== 'EXPRESS_ACCEPT' &&
    draftOfferData.type !== 'BENCHMARK' &&
    draftOfferData.type !== 'STATIC'
  ) {
    return (
      <DraftOfferError
        errorType="INVALID"
        trackEventMessage={`Draft offer type (${draftOfferData.type}) is not valid for draft submission.`}
        draftType={draftOfferData.draftType}
        type={draftOfferData.type}
      />
    );
  }

  // Validate that we have rate informations (empty array means the API returned no rate data for a
  // READY_FOR_SUBMISSION draft, which is an unsupported/malformed state)
  if (draftOfferData.rateInformations.length === 0) {
    return (
      <DraftOfferError
        errorType="INVALID"
        trackEventMessage="Draft offer has no rate informations, which is not supported."
        draftType={draftOfferData.draftType}
        type={draftOfferData.type}
      />
    );
  }

  // Validate that all markets have the same rate type
  const rateTypes = new Set(draftOfferData.rateInformations.map((ri) => ri.rateInformation.rateType));
  if (rateTypes.size > 1) {
    return (
      <DraftOfferError
        errorType="INVALID"
        trackEventMessage="Draft offer has mixed rate types across markets, which is not supported."
        draftType={draftOfferData.draftType}
        type={draftOfferData.type}
      />
    );
  }

  return <DraftOfferCore draftOfferData={draftOfferData} />;
};

const DraftOfferReview = () => {
  const { t } = useTranslation();

  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="flex flex-col gap-6 pb-10">
          <h1 className="m-0 text-4xl">{t('core.earlyPayOffer')}</h1>
          <OfferReviewLayoutSkeleton />
        </div>
      )}
      ErrorComponent={() => (
        <DraftOfferError
          errorType="INVALID"
          trackEventMessage="An unknown error occurred while rendering draft offer, hitting the error boundary."
        />
      )}
    >
      <DraftOfferComponent />
    </QueryBoundaries>
  );
};

export default DraftOfferReview;
